import {
  Button,
  ButtonProps,
  DatePicker,
  Flex,
  InputNumber,
  Radio,
  Select,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Toolbox } from '~/plugins/toolbox/shared'
import { EndsScope, RepeatScope, useRRule } from './hooks'

type ValueType = {
  rule?: string
  status?: 'created' | 'stopped' | 'active'
  dateNext?: string
}

type Props = {
  id?: string
  value?: ValueType
  initialValues?: ValueType
  formValuesConfiguration?: any
  onChange?: (value: ValueType[]) => void
  type?: 'DAILY'
}

const ButtonDay = ({ children, title, style, type, ...props }: ButtonProps) => {
  return (
    <Tooltip title={title} arrow={false}>
      <Button
        size="small"
        shape="circle"
        title={title}
        {...props}
        style={{ boxShadow: 'none', ...style }}
      >
        {children}
      </Button>
    </Tooltip>
  )
}

export const RRulePicker: React.FC<Props> = ({
  id,
  value,
  initialValues,
  formValuesConfiguration = {},
  type,
  onChange,
}) => {
  const { timezone } = formValuesConfiguration

  const isTypeDaily = type === 'DAILY'

  const [isReady, setReady] = useState(false)
  const [rule, setRule] = useState<string>()
  const [startsDate, setStartsDate] = useState<dayjs.Dayjs>(
    dayjs().startOf('day'),
  )
  const [startsHour, setStartsHour] = useState<number>(
    isTypeDaily ? undefined : dayjs().hour(),
  )
  const [repeatCount, setRepeatCount] = useState(1)
  const [repeatScope, setRepeatScope] = useState<RepeatScope>('days')
  const [repeatDays, setRepeatDays] = useState<number[]>([])
  const [endsScope, setEndsScope] = useState<EndsScope>('never')
  const [endsDate, setEndsDate] = useState<dayjs.Dayjs>()
  const [endsAfterCount, setEndsAfterCount] = useState<number>(10)

  const { build, parse } = useRRule({ timezone })

  const handleChange = () => {
    try {
      const rule = build({
        startsDate,
        startsHour,
        repeatCount,
        repeatScope,
        repeatDays,
        endsScope,
        endsDate,
        endsAfterCount,
      })

      if (!isReady) {
        return
      }

      setRule(rule)

      if (value?.rule !== rule) {
        const valueUpdated: ValueType = {
          ...(value ?? {}),
          rule,
          status: 'created',
        }

        onChange([valueUpdated])
      }
    } catch (error) {
      // ignore
      console.error('Unknown Error', error)
    }
  }

  useEffect(() => {
    handleChange()
  }, [
    // isReady,
    startsDate,
    startsHour,
    repeatCount,
    repeatScope,
    repeatDays,
    endsScope,
    endsDate,
    endsAfterCount,
  ])

  useEffect(() => {
    try {
      if (value?.rule === rule) {
        setReady(true)
        return
      }

      setRule(value?.rule)

      if (!Toolbox.Utility.isNull(value?.rule)) {
        const values = parse(value?.rule)

        setStartsDate(values.startsDate)
        setStartsHour(values.startsHour)
        setRepeatCount(values.repeatCount)
        setRepeatScope(values.repeatScope)
        setRepeatDays(values.repeatDays)
        setEndsScope(values.endsScope)
        setEndsDate(values.endsDate)
        setEndsAfterCount(values.endsAfterCount)
      }
    } catch (error) {
      // ignore
      console.log(error)
    }

    setReady(true)
  }, [value])

  const handleClickDay = (dayNumber: number) => {
    setRepeatDays(values => {
      if (values.includes(dayNumber)) {
        return values.filter(candidate => candidate !== dayNumber)
      } else {
        return [...values, dayNumber].sort()
      }
    })
  }

  const repeatDaysOptions = [
    {
      title: 'Sunday',
      content: 'S',
    },
    {
      title: 'Monday',
      content: 'M',
    },
    {
      title: 'Tuesday',
      content: 'T',
    },
    {
      title: 'Wednesday',
      content: 'W',
    },
    {
      title: 'Thrusday',
      content: 'T',
    },
    {
      title: 'Friday',
      content: 'F',
    },
    {
      title: 'Saturday',
      content: 'S',
    },
  ]

  const SelectHour = () => (
    <Select
      value={startsHour}
      variant="filled"
      style={{ width: '110px' }}
      onChange={setStartsHour}
      placeholder="Select"
      options={[
        ...Array.from({ length: 4 }, (_, index) => ({
          label: `${index + 8} AM`,
          value: index + 8,
        })),
        { label: 'Noon', value: 12 },
        ...Array.from({ length: 11 }, (_, index) => ({
          label: `${index + 1} PM`,
          value: index + 13,
        })),
        { label: 'Midnight', value: 0 },

        ...Array.from({ length: 7 }, (_, index) => ({
          label: `${index + 1} AM`,
          value: index + 1,
        })),
      ]}
    />
  )

  if (isTypeDaily) {
    return (
      <span id={id}>
        <SelectHour />
      </span>
    )
  }

  return (
    <span id={id}>
      <Flex vertical gap={8}>
        <Flex align="center" gap={8}>
          <Typography.Text type="secondary">Start on</Typography.Text>

          <DatePicker
            value={startsDate?.startOf('day')}
            variant="filled"
            format={'DD MMM YYYY'}
            style={{ width: '150px' }}
            onChange={date => setStartsDate(date.startOf('day'))}
          />

          <SelectHour />
        </Flex>

        <Flex align="center" gap={8}>
          <Typography.Text type="secondary">Repeat every</Typography.Text>
          <InputNumber
            value={repeatCount}
            min={1}
            variant="filled"
            style={{ width: '68px' }}
            onChange={count => setRepeatCount(count as number)}
          />
          <Select
            value={repeatScope}
            variant="filled"
            style={{ width: '110px' }}
            onChange={setRepeatScope}
            options={[
              {
                label: 'days',
                value: 'days',
              },
              {
                label: 'weeks',
                value: 'weeks',
              },
              {
                label: 'months',
                value: 'months',
              },
            ]}
          />
        </Flex>
        {repeatScope === 'weeks' && (
          <Flex justify="center" vertical gap={8}>
            <Typography.Text type="secondary">Repeat on</Typography.Text>

            <Flex gap={8}>
              {repeatDaysOptions.map((option, index) => {
                const isSelected = repeatDays?.includes(index)
                return (
                  <ButtonDay
                    key={index}
                    title={option.title}
                    type={isSelected ? 'primary' : undefined}
                    onClick={() => handleClickDay(index)}
                  >
                    {option.content}
                  </ButtonDay>
                )
              })}
            </Flex>
          </Flex>
        )}
        <Flex justify="center" vertical gap={8}>
          <Typography.Text type="secondary">Ends</Typography.Text>

          <Flex>
            <Flex style={{ width: '100px' }}>
              <Radio.Group
                value={endsScope}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
                onChange={event => setEndsScope(event.target.value)}
                options={[
                  { value: 'never', label: 'Never' },
                  { value: 'on', label: 'On' },
                  { value: 'after', label: 'After' },
                ]}
              />
            </Flex>
            <Flex flex={1} vertical>
              {endsScope === 'on' && (
                <DatePicker
                  value={endsDate}
                  variant="filled"
                  style={{ width: '180px' }}
                  onChange={date => setEndsDate(date)}
                />
              )}

              {endsScope === 'after' && (
                <InputNumber
                  value={endsAfterCount}
                  min={1}
                  variant="filled"
                  style={{ width: '180px' }}
                  addonAfter="occurence(s)"
                  onChange={count => setEndsAfterCount(count as number)}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </span>
  )
}
